import React from 'react';
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ExternalLink, Star } from "lucide-react";
import g2Rank1Image from "@/assets/g2-rank1.webp";
import g2Leader from "@/assets/badges/g2-leader.svg";
import g2BestResults from "@/assets/badges/g2-best-results.svg";
import g2HighPerformer from "@/assets/badges/g2-high-performer.svg";
import g2MomentumLeader from "@/assets/badges/g2-momentum-leader.svg";
import g2BestROI from "@/assets/badges/g2-best-roi.svg";

const G2RecognitionSection = () => {
  return (
    <div className="py-12 sm:py-16 2xl:py-20 px-4 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Text and Badges */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
              Ranked <span className="text-primary">#1 by our customers</span>... for a reason.
            </h2>
            
            <p className="text-base text-gray-600 leading-relaxed">
              Ranked #1 on G2 with a 9.5/10 usability score. Recognized for delivering the best results, fastest adoption, and highest ROI in employee recognition.
            </p>

            {/* Badge Lineup - Desktop */}
            <div className="hidden sm:flex items-center justify-start gap-3 flex-wrap">
              {/* Smaller badge - Momentum Leader (left) */}
              <motion.img
                src={g2MomentumLeader}
                alt="G2 Momentum Leader"
                className="h-20 w-auto hover:scale-105 transition-transform duration-300"
                whileHover={{ y: -5 }}
              />
              
              {/* Larger badges - Center 3 */}
              <motion.img
                src={g2Leader}
                alt="G2 Leader"
                className="h-24 w-auto hover:scale-105 transition-transform duration-300"
                whileHover={{ y: -5 }}
              />
              <motion.img
                src={g2BestResults}
                alt="G2 Best Results"
                className="h-24 w-auto hover:scale-105 transition-transform duration-300"
                whileHover={{ y: -5 }}
              />
              <motion.img
                src={g2HighPerformer}
                alt="G2 High Performer"
                className="h-24 w-auto hover:scale-105 transition-transform duration-300"
                whileHover={{ y: -5 }}
              />
              
              {/* Smaller badge - Best ROI (right) */}
              <motion.img
                src={g2BestROI}
                alt="G2 Best ROI"
                className="h-20 w-auto hover:scale-105 transition-transform duration-300"
                whileHover={{ y: -5 }}
              />
            </div>

            {/* Badge Lineup - Mobile (Horizontal Scroll) */}
            <div className="sm:hidden overflow-x-auto pb-4">
              <div className="flex items-center gap-3 min-w-max px-2">
                <img src={g2MomentumLeader} alt="G2 Momentum Leader" className="h-16 w-auto" />
                <img src={g2Leader} alt="G2 Leader" className="h-20 w-auto" />
                <img src={g2BestResults} alt="G2 Best Results" className="h-20 w-auto" />
                <img src={g2HighPerformer} alt="G2 High Performer" className="h-20 w-auto" />
                <img src={g2BestROI} alt="G2 Best ROI" className="h-16 w-auto" />
              </div>
            </div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="pt-2"
            >
            <Button
              asChild
              variant="outline"
              size="lg"
              className="font-semibold border-2 hover:bg-primary hover:text-white hover:border-primary transition-all duration-300"
            >
              <a
                href="https://www.g2.com/products/assembly/reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <span>Check it out in G2</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
            </motion.div>
          </motion.div>

          {/* Right Column - Visual */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-3 max-w-xl mx-auto lg:mx-0"
          >
            <img
              src={g2Rank1Image}
              alt="Assembly ranked #1 on G2 for Usability"
              className="w-full h-auto rounded-2xl"
            />
            <p className="text-xs text-center text-gray-600 font-medium">
              Assembly outperforms top recognition platforms - ranked #1
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default G2RecognitionSection;
